
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface ExportDiarioPDFOptions {
  diario: any
  obra?: any
  funcionarios?: any[]
  equipamentos?: any[]
}

export const exportDiarioPDF = ({ diario, obra, funcionarios = [], equipamentos = [] }: ExportDiarioPDFOptions) => {
  try {
    // Criar novo documento PDF
    const doc = new jsPDF('portrait', 'mm', 'a4')
    
    // Configurações
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20
    
    // Título principal
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text('Diário de Obra', margin, 25)
    
    // Informações da obra
    if (obra) {
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text(`Obra: ${obra.nomeObra}`, margin, 40)
      
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Contrato: ${obra.numeroContrato || 'N/A'}`, margin, 48)
      doc.text(`Responsável: ${obra.responsavelNome || 'N/A'}`, margin, 54)
    }
    
    // Data do diário
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text(`Data: ${new Date(diario.data).toLocaleDateString('pt-BR')}`, margin, 65)
    
    // Data de geração
    const dataGeracao = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    doc.text(`Gerado em: ${dataGeracao}`, pageWidth - 80, 48)
    
    // Informações do diário
    let currentY = 80
    
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('Informações Gerais:', margin, currentY)
    
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    currentY += 8
    doc.text(`Turno: ${diario.turno}`, margin, currentY)
    currentY += 6
    doc.text(`Clima: ${diario.clima}`, margin, currentY)
    currentY += 6
    doc.text(`Temperatura: ${diario.temperatura}°C`, margin, currentY)
    currentY += 6
    doc.text(`Responsável: ${diario.responsavel}`, margin, currentY)
    
    // Atividades realizadas
    if (diario.atividadesRealizadas && diario.atividadesRealizadas.length > 0) {
      currentY += 15
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Atividades Realizadas:', margin, currentY)
      
      // Preparar dados da tabela de atividades
      const atividadesData = diario.atividadesRealizadas.map((atividade: any) => [
        atividade.descricao || 'N/A',
        atividade.etapa || 'N/A',
        atividade.progresso ? `${atividade.progresso}%` : '0%',
        atividade.responsavel || 'N/A'
      ])
      
      currentY += 10
      autoTable(doc, {
        startY: currentY,
        head: [['Atividade', 'Etapa', 'Progresso', 'Responsável']],
        body: atividadesData,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        margin: { left: margin, right: margin }
      })
      
      currentY = (doc as any).lastAutoTable.finalY + 10
    }
    
    // Funcionários presentes
    if (diario.funcionariosPresentes && diario.funcionariosPresentes.length > 0) {
      // Verificar se precisa de nova página
      if (currentY > pageHeight - 60) {
        doc.addPage()
        currentY = 20
      }
      
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Funcionários Presentes:', margin, currentY)
      
      // Preparar dados da tabela de funcionários
      const funcionariosData = diario.funcionariosPresentes.map((fp: any) => {
        const funcionario = funcionarios.find(f => f._id === fp.funcionarioId)
        return [
          funcionario?.nome || fp.nome || 'N/A',
          funcionario?.cargo || fp.cargo || 'N/A',
          fp.horaEntrada || 'N/A',
          fp.horaSaida || 'N/A',
          fp.horasTrabalhadas ? `${fp.horasTrabalhadas}h` : 'N/A'
        ]
      })
      
      currentY += 10
      autoTable(doc, {
        startY: currentY,
        head: [['Nome', 'Cargo', 'Entrada', 'Saída', 'Horas']],
        body: funcionariosData,
        theme: 'striped',
        headStyles: {
          fillColor: [16, 185, 129],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        margin: { left: margin, right: margin }
      })
      
      currentY = (doc as any).lastAutoTable.finalY + 10
    }
    
    // Equipamentos utilizados
    if (diario.equipamentosUtilizados && diario.equipamentosUtilizados.length > 0) {
      // Verificar se precisa de nova página
      if (currentY > pageHeight - 60) {
        doc.addPage()
        currentY = 20
      }
      
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Equipamentos Utilizados:', margin, currentY)
      
      // Preparar dados da tabela de equipamentos
      const equipamentosData = diario.equipamentosUtilizados.map((eq: any) => {
        const equipamento = equipamentos.find(e => e._id === eq.equipamentoId)
        return [
          equipamento?.nome || eq.nome || 'N/A',
          equipamento?.tipo || eq.tipo || 'N/A',
          eq.horasUtilizacao ? `${eq.horasUtilizacao}h` : 'N/A',
          eq.combustivel ? `${eq.combustivel}L` : 'N/A',
          eq.observacoes || 'N/A'
        ]
      })
      
      currentY += 10
      autoTable(doc, {
        startY: currentY,
        head: [['Equipamento', 'Tipo', 'Horas', 'Combustível', 'Observações']],
        body: equipamentosData,
        theme: 'striped',
        headStyles: {
          fillColor: [245, 158, 11],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        margin: { left: margin, right: margin }
      })
      
      currentY = (doc as any).lastAutoTable.finalY + 10
    }
    
    // Materiais consumidos
    if (diario.materiaisConsumidos && diario.materiaisConsumidos.length > 0) {
      // Verificar se precisa de nova página
      if (currentY > pageHeight - 60) {
        doc.addPage()
        currentY = 20
      }
      
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Materiais Consumidos:', margin, currentY)
      
      // Preparar dados da tabela de materiais
      const materiaisData = diario.materiaisConsumidos.map((material: any) => [
        material.nome || 'N/A',
        material.quantidade ? `${material.quantidade}` : '0',
        material.unidade || 'UN',
        material.fornecedor || 'N/A'
      ])
      
      currentY += 10
      autoTable(doc, {
        startY: currentY,
        head: [['Material', 'Quantidade', 'Unidade', 'Fornecedor']],
        body: materiaisData,
        theme: 'striped',
        headStyles: {
          fillColor: [139, 92, 246],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        margin: { left: margin, right: margin }
      })
      
      currentY = (doc as any).lastAutoTable.finalY + 10
    }
    
    // Observações
    if (diario.observacoes) {
      // Verificar se precisa de nova página
      if (currentY > pageHeight - 40) {
        doc.addPage()
        currentY = 20
      }
      
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Observações:', margin, currentY)
      
      currentY += 10
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      
      // Quebrar texto em múltiplas linhas
      const observacoesLines = doc.splitTextToSize(diario.observacoes, pageWidth - 2 * margin)
      doc.text(observacoesLines, margin, currentY)
    }
    
    // Rodapé
    const totalPages = doc.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text(
        `Página ${i} de ${totalPages} - Diário de Obra - ${new Date(diario.data).toLocaleDateString('pt-BR')}`,
        margin,
        pageHeight - 10
      )
    }
    
    // Gerar nome do arquivo
    const nomeArquivo = `diario_obra_${new Date(diario.data).toISOString().split('T')[0]}_${diario.turno.replace(/\s+/g, '_').toLowerCase()}.pdf`
    
    // Fazer download
    doc.save(nomeArquivo)
    
    return true
  } catch (error) {
    console.error('Erro ao gerar PDF do diário:', error)
    throw new Error('Falha na geração do PDF')
  }
}

export const exportDiariosSemanalPDF = (diarios: any[], obra?: any, funcionarios: any[] = [], equipamentos: any[] = []) => {
  try {
    // Criar novo documento PDF
    const doc = new jsPDF('landscape', 'mm', 'a4')
    
    // Configurações
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20
    
    // Título principal
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text('Relatório Semanal - Diários de Obra', margin, 25)
    
    // Informações da obra
    if (obra) {
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text(`Obra: ${obra.nomeObra}`, margin, 40)
      
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Contrato: ${obra.numeroContrato || 'N/A'}`, margin, 48)
      doc.text(`Responsável: ${obra.responsavelNome || 'N/A'}`, margin, 54)
    }
    
    // Data de geração
    const dataGeracao = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    doc.text(`Gerado em: ${dataGeracao}`, pageWidth - 80, 48)
    
    // Período
    if (diarios.length > 0) {
      const primeiraData = new Date(Math.min(...diarios.map(d => new Date(d.data).getTime())))
      const ultimaData = new Date(Math.max(...diarios.map(d => new Date(d.data).getTime())))
      
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text(`Período: ${primeiraData.toLocaleDateString('pt-BR')} a ${ultimaData.toLocaleDateString('pt-BR')}`, margin, 65)
    }
    
    // Resumo dos diários
    const resumoData = diarios.map(diario => [
      new Date(diario.data).toLocaleDateString('pt-BR'),
      diario.turno,
      diario.clima,
      `${diario.temperatura}°C`,
      diario.responsavel,
      diario.atividadesRealizadas?.length || 0,
      diario.funcionariosPresentes?.length || 0,
      diario.equipamentosUtilizados?.length || 0
    ])
    
    autoTable(doc, {
      startY: 80,
      head: [['Data', 'Turno', 'Clima', 'Temp.', 'Responsável', 'Atividades', 'Funcionários', 'Equipamentos']],
      body: resumoData,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25 },
        3: { cellWidth: 20 },
        4: { cellWidth: 40 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
        7: { cellWidth: 30 }
      },
      margin: { left: margin, right: margin }
    })
    
    // Estatísticas
    const finalY = (doc as any).lastAutoTable.finalY + 15
    
    if (finalY < pageHeight - 50) {
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Estatísticas do Período:', margin, finalY)
      
      const stats = {
        totalDiarios: diarios.length,
        totalAtividades: diarios.reduce((acc, d) => acc + (d.atividadesRealizadas?.length || 0), 0),
        totalFuncionarios: new Set(diarios.flatMap(d => d.funcionariosPresentes?.map((fp: any) => fp.funcionarioId) || [])).size,
        totalEquipamentos: new Set(diarios.flatMap(d => d.equipamentosUtilizados?.map((eq: any) => eq.equipamentoId) || [])).size,
        diasComChuva: diarios.filter(d => d.clima?.toLowerCase().includes('chuva')).length,
        temperatureMedia: diarios.reduce((acc, d) => acc + (d.temperatura || 0), 0) / diarios.length
      }
      
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text(`Total de Diários: ${stats.totalDiarios}`, margin, finalY + 10)
      doc.text(`Total de Atividades: ${stats.totalAtividades}`, margin + 70, finalY + 10)
      doc.text(`Funcionários Diferentes: ${stats.totalFuncionarios}`, margin + 140, finalY + 10)
      doc.text(`Equipamentos Utilizados: ${stats.totalEquipamentos}`, margin + 210, finalY + 10)
      doc.text(`Dias com Chuva: ${stats.diasComChuva}`, margin, finalY + 18)
      doc.text(`Temperatura Média: ${stats.temperatureMedia.toFixed(1)}°C`, margin + 70, finalY + 18)
    }
    
    // Gerar nome do arquivo
    const nomeArquivo = `relatorio_semanal_diarios_${new Date().toISOString().split('T')[0]}.pdf`
    
    // Fazer download
    doc.save(nomeArquivo)
    
    return true
  } catch (error) {
    console.error('Erro ao gerar relatório semanal:', error)
    throw new Error('Falha na geração do relatório semanal')
  }
}

// Manter a função existente do planejamento semanal
export const exportPlanejamentoSemanalPDF = ({ obra, atividades, funcionarios }: any) => {
  try {
    // Criar novo documento PDF
    const doc = new jsPDF('landscape', 'mm', 'a4')
    
    // Configurações
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20
    
    // Título principal
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text('Planejamento Semanal de Obra', margin, 25)
    
    // Informações da obra
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text(`Obra: ${obra.nomeObra}`, margin, 40)
    
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Contrato: ${obra.numeroContrato || 'N/A'}`, margin, 48)
    doc.text(`Responsável: ${obra.responsavelNome || 'N/A'}`, margin, 54)
    doc.text(`Status: ${obra.status?.replace('_', ' ').toUpperCase() || 'N/A'}`, margin, 60)
    
    // Data de geração
    const dataGeracao = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    doc.text(`Gerado em: ${dataGeracao}`, pageWidth - 80, 48)
    
    // Resumo financeiro
    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0)
    }
    
    const resumoY = 75
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('Resumo Financeiro:', margin, resumoY)
    
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text(`Valor Orçado: ${formatCurrency(obra.valorOrcado)}`, margin, resumoY + 8)
    doc.text(`Valor Realizado: ${formatCurrency(obra.valorRealizado)}`, margin + 70, resumoY + 8)
    doc.text(`Progresso: ${obra.progressoGeral?.toFixed(1) || 0}%`, margin + 140, resumoY + 8)
    doc.text(`Custos Administrativos: ${formatCurrency(obra.custosAdministrativos)}`, margin + 190, resumoY + 8)
    
    // Calcular lucro líquido
    const lucroLiquido = (obra.valorOrcado || 0) - (obra.valorRealizado || 0) - (obra.custosAdministrativos || 0)
    doc.text(`Lucro Líquido: ${formatCurrency(lucroLiquido)}`, margin, resumoY + 16)
    
    // Filtrar atividades da obra
    const atividadesObra = atividades.filter((a: any) => a.obraId === obra._id)
    
    // Preparar dados da tabela
    const tableData = atividadesObra.map((atividade: any) => {
      const funcionario = funcionarios.find((f: any) => f._id === atividade.funcionarioId)
      const etapaVinculada = obra.etapas?.find((e: any) => e.id === atividade.etapaId)
      const subetapaVinculada = etapaVinculada?.subetapas?.find((s: any) => s.id === atividade.subetapaId)
      
      let etapaInfo = 'Não vinculada'
      if (etapaVinculada) {
        etapaInfo = `Etapa ${etapaVinculada.numero}`
        if (subetapaVinculada) {
          etapaInfo += ` > Sub ${subetapaVinculada.numero}`
        }
      }
      
      return [
        atividade.descricao || 'N/A',
        etapaInfo,
        funcionario?.nome || atividade.funcionarioResponsavel || 'N/A',
        atividade.status?.replace('_', ' ').toUpperCase() || 'N/A',
        `${atividade.progresso || 0}%`,
        atividade.prazo ? new Date(atividade.prazo).toLocaleDateString('pt-BR') : 'N/A',
        atividade.prioridade?.toUpperCase() || 'N/A'
      ]
    })
    
    // Configurar tabela
    const tableStartY = resumoY + 30
    
    autoTable(doc, {
      startY: tableStartY,
      head: [['Tarefa', 'Etapa/Subetapa', 'Responsável', 'Status', 'Progresso', 'Prazo', 'Prioridade']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 35 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 }
      },
      margin: { left: margin, right: margin }
    })
    
    // Gerar nome do arquivo
    const nomeArquivo = `planejamento_semanal_${obra.nomeObra.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
    
    // Fazer download
    doc.save(nomeArquivo)
    
    return true
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    throw new Error('Falha na geração do PDF')
  }
}
